import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import globalStyles from './styles/outline-footer.global.pcss?inline';
import encapsulatedStyles from './styles/outline-footer.encapsulated.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import {screenSizeMap} from '../../controllers/screen-size-controller';
import '../outline-logo/outline-logo';
import '../outline-container/outline-container';
/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-footer';

/**
 * The Outline Core Footer component
 *
 * A reusable LitElement component for displaying footers.
 *
 * @element outline-footer
 * @extends LitElement
 */
@customElement(componentName)
export class OutlineFooter extends LitElement {
  resizeObserver: ResizeObserver;
  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  @property({type: Boolean, reflect: true})
  isMobile: boolean;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resetAtScreen();
    });
  }

  firstUpdated() {
    const self = this.shadowRoot?.querySelector('.outline-footer') as HTMLElement;
    this.resizeObserver.observe(self);
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  resetAtScreen() {
    this.isMobile = window.innerWidth < (screenSizeMap.get('xl') as number);
  }

  render(): TemplateResult {
    return html`
    <footer class="outline-footer">
      <outline-container background-color="blue" container-width="full-bleed">
        <outline-container>
          <div class="outline-footer--top-rail">
            <div class="outline-footer--logo-directory">
              <div class="outline-footer--logo">
                <outline-logo variant="seal"></outline-logo>
              </div>
              <div class="outline-footer--directory-link">
                <slot name='directory-link'></slot>
              </div>
            </div>
            <div class="outline-footer--cta">
              <slot name="cta"></slot>
            </div>
          </div>
        </outline-container>
      </outline-container>
      ${this.isMobile ? this.mobileTemplate() : this.desktopTemplate()}
      <outline-container container-width="full-bleed" background-color="blue">
        <div class="outline-footer--legal">
          <div class="outline-footer--promo-link">
            <slot name="promo"></slot>
          </div>
          <div class="outline-footer--copy-links">
            <slot name="copyright"></slot>
            <slot name="legal-site"></slot>
          </div>
        </div>
      </outline-container>
    </footer>
    `;
  }

  mobileTemplate() {
    return html`
      <outline-container container-width="full-bleed" background-color="navy">
        <outline-container>
          <div class="outline-footer--main-content">
           
            <div class="outline-footer--visitor-info">
              <slot name="visiting"></slot>
            </div>
            <div class="outline-footer--resources">
              <slot name="resources"></slot>
            </div>

            <div class="outline-footer--social-contact">
              <div class="outline-footer--socials">
                <slot name="socials"></slot>
              </div>
              <div class="outline-footer--contact-info">
                <div class="outline-footer--address">
                  <slot name='add-line-1'></slot>
                </div>
                <div class="outline-footer--phone">
                  <slot name='phone'></slot>
                </div>
              </div>
            </div>
          </div>
        </outline-container>
      </outline-container>
    `
  }

  desktopTemplate() {
    return html`
    <outline-container container-width="full-bleed" background-color="navy">
      <outline-container container-width="default">
        <div class="outline-footer--main-content">
          <div class="outline-footer--social-contact">
            <div class="outline-footer--socials">
              <slot name="socials"></slot>
            </div>
            <div class="outline-footer--contact-info">
              <div class="outline-footer--address">
                <slot name='add-line-1'></slot>
              </div>
              <div class="outline-footer--phone">
                <slot name='phone'></slot>
              </div>
            </div>
          </div>
          <div class="outline-footer--visitor-info">
            <slot name="visiting"></slot>
          </div>
          <div class="outline-footer--resources">
            <slot name="resources"></slot>
          </div>
        </div>
      </outline-container>
    </outline-container>
  `;
  }
}



/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineFooter;
  }
}
